/// Leitura da linha digitável no navegador — espelho de `payment-slip.ts` do módulo no servidor.
///
/// A cópia é deliberada. O servidor continua sendo a autoridade: nada é gravado sem passar pela
/// validação dele. O que mora aqui é o que torna a CONFERÊNCIA possível — o operador cola os 47
/// dígitos e vê na hora o vencimento e o valor que aquele número carrega, sem uma ida ao servidor
/// por tecla digitada. Trazer o módulo inteiro para o pacote compartilhado só por causa destas
/// linhas furaria a fronteira do módulo, que é o que permite vendê-lo à parte.
///
/// O algoritmo é padrão FEBRABAN e não muda; as duas cópias são fixadas pelas MESMAS parcelas de um
/// carnê real nos testes dos dois lados, e é isso que denuncia qualquer divergência.

export interface DecodedSlip {
  /// Só os 47 dígitos, sem a pontuação de impressão.
  line: string;
  dueOn: string | null;
  amountCents: number;
}

const LINE_LENGTH = 47;
/// Fator 1000 = 22/02/2025, início do ciclo atual da FEBRABAN.
const CYCLE_BASE = Date.UTC(2025, 1, 22);
const FIRST_FACTOR = 1000;
const MS_PER_DAY = 86_400_000;

export function digitsOnly(value: string): string {
  return value.replace(/\D/gu, '');
}

function mod10(field: string): number {
  let sum = 0;
  let weight = 2;
  for (let index = field.length - 1; index >= 0; index -= 1) {
    const product = Number(field[index]) * weight;
    sum += product > 9 ? product - 9 : product;
    weight = weight === 2 ? 1 : 2;
  }
  return (10 - (sum % 10)) % 10;
}

function mod11(withoutCheckDigit: string): number {
  let sum = 0;
  let weight = 2;
  for (let index = withoutCheckDigit.length - 1; index >= 0; index -= 1) {
    sum += Number(withoutCheckDigit[index]) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  const checkDigit = 11 - (sum % 11);
  return checkDigit < 2 || checkDigit > 9 ? 1 : checkDigit;
}

function barcodeOf(digits: string): string {
  return (
    digits.slice(0, 4) +
    digits.slice(32, 33) +
    digits.slice(33, 47) +
    digits.slice(4, 9) +
    digits.slice(10, 20) +
    digits.slice(21, 31)
  );
}

export function slipLineIsValid(value: string): boolean {
  const digits = digitsOnly(value);
  if (digits.length !== LINE_LENGTH) return false;

  const fields: Array<[string, string]> = [
    [digits.slice(0, 9), digits.slice(9, 10)],
    [digits.slice(10, 20), digits.slice(20, 21)],
    [digits.slice(21, 31), digits.slice(31, 32)],
  ];
  if (fields.some(([field, checkDigit]) => mod10(field) !== Number(checkDigit))) return false;

  const barcode = barcodeOf(digits);
  return mod11(barcode.slice(0, 4) + barcode.slice(5)) === Number(barcode.slice(4, 5));
}

export function decodeSlipLine(value: string): DecodedSlip | null {
  const digits = digitsOnly(value);
  if (!slipLineIsValid(digits)) return null;

  const factor = Number(digits.slice(33, 37));
  const dueOn =
    factor === 0
      ? null
      : new Date(CYCLE_BASE + (factor - FIRST_FACTOR) * MS_PER_DAY).toISOString().slice(0, 10);

  return { line: digits, dueOn, amountCents: Number(digits.slice(37, 47)) };
}

/// Formata como vem impresso no boleto, para conferir dígito a dígito.
export function formatSlipLine(value: string): string {
  const digits = digitsOnly(value);
  if (digits.length !== LINE_LENGTH) return value;
  return [
    `${digits.slice(0, 5)}.${digits.slice(5, 10)}`,
    `${digits.slice(10, 15)}.${digits.slice(15, 21)}`,
    `${digits.slice(21, 26)}.${digits.slice(26, 32)}`,
    digits.slice(32, 33),
    digits.slice(33, 47),
  ].join(' ');
}
